import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import AppLoader from '../../../components/AppLoader/AppLoader';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import eOpsLogo from '../../../images/eops.png';
import pitcLogo from '../../../images/pitcLogo.png';
import usLogo from '../../../images/usLogo.png';
import './MfaStep.scss';

const OTP_LEN = 6;

function MfaStep({
  variant = 'fullscreen',
  qrSrc,
  manualSecret,
  qrLoading,
  qrError,
  verifying,
  verifyError,
  onVerify,
  onCancel,
}) {
  const [digits, setDigits] = useState(() => Array(OTP_LEN).fill(''));
  const inputsRef = useRef([]);

  useEffect(() => {
    inputsRef.current = inputsRef.current.slice(0, OTP_LEN);
  }, []);

  useEffect(() => {
    if (variant !== 'dialog') return;
    const t = setTimeout(() => inputsRef.current[0]?.focus?.(), 150);
    return () => clearTimeout(t);
  }, [variant]);

  const focusInput = (index) => {
    inputsRef.current[index]?.focus?.();
  };

  const handleDigitChange = (index, raw) => {
    const v = raw.replace(/\D/g, '').slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = v;
      return next;
    });
    if (v && index < OTP_LEN - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const handlePaste = useCallback((e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LEN);
    if (!text) return;
    e.preventDefault();
    const arr = text.split('').concat(Array(OTP_LEN).fill('')).slice(0, OTP_LEN);
    setDigits(arr);
    const nextEmpty = arr.findIndex((d) => !d);
    focusInput(nextEmpty === -1 ? OTP_LEN - 1 : nextEmpty);
  }, []);

  const otpComplete = digits.every((d) => d.length === 1);

  const handleVerifyClick = () => {
    if (!otpComplete) return;
    onVerify(digits.join(''));
  };

  const rootClass =
    variant === 'dialog' ? 'mfa-step-root mfa-step-root--dialog' : 'mfa-step-root';
  const cardClass =
    variant === 'dialog' ? 'mfa-card mfa-card--dialog' : 'mfa-card';

  return (
    <Box className={rootClass}>
      {/* {variant === 'dialog' ? (
        <IconButton
          type="button"
          className="mfa-dialog-close"
          onClick={onCancel}
          size="small"
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      ) : null} */}
      <Grid container className={cardClass} spacing={0} wrap="wrap">
        <Grid item xs={12} lg={7} className="mfa-col mfa-col--interactive">
          <Box className="mfa-logo-wrap">
            <img src={eOpsLogo} alt="eOps" className="mfa-logo" />
          </Box>
          <Typography component="h1" className="mfa-title">
            Multi-Factor Authentication
          </Typography>
          <Typography className="mfa-lead">
            Scan the QR code using your authenticator app (Microsoft Authenticator or Google
            Authenticator) and enter the 6-digit code to continue.
          </Typography>

          {(qrLoading || qrSrc) && (
            <Box className="mfa-qr-wrap">
              {qrLoading ? (
                <Box className="mfa-qr-placeholder">
                  <AppLoader size={40} />
                </Box>
              ) : (
                <img src={qrSrc} alt="Authenticator QR" className="mfa-qr-img" />
              )}
            </Box>
          )}

          <Box className={`mfa-otp-block ${!qrLoading && !qrSrc ? 'mfa-otp-block--noqr' : ''}`}>
            <Typography className="mfa-otp-label">Enter 6-digit OTP</Typography>
            <Box className="mfa-otp-row" onPaste={handlePaste}>
              {digits.map((d, i) => (
                <TextField
                  key={i}
                  inputRef={(el) => {
                    inputsRef.current[i] = el;
                  }}
                  value={d}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  inputProps={{
                    maxLength: 1,
                    inputMode: 'numeric',
                    'aria-label': `Digit ${i + 1}`,
                    style: { textAlign: 'center', fontSize: '1.125rem', fontWeight: 600 },
                  }}
                  size="small"
                  className="mfa-otp-box"
                />
              ))}
            </Box>
          </Box>

          {verifyError ? (
            <Alert severity="error" sx={{ mt: 1.5, width: '100%' }}>
              {verifyError}
            </Alert>
          ) : null}

          <Button
            fullWidth
            variant="contained"
            className="mfa-verify-btn"
            disabled={!otpComplete || verifying}
            onClick={handleVerifyClick}
          >
            {verifying ? (
              <AppLoader size={26} />
            ) : (
              'Verify & Sign In'
            )}
          </Button>

          {/* <Accordion className="mfa-accordion" disableGutters elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <HelpOutlineIcon className="mfa-accordion-icon" />
              <Typography className="mfa-accordion-summary-text">
                Can&apos;t scan the QR code? Enter the code below manually in your authenticator app.
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Add an account manually and enter this secret key:
              </Typography>
              <Box className="mfa-secret-box">
                {manualSecret ? (
                  <Typography component="code" className="mfa-secret-code">
                    {manualSecret}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Secret will appear when the QR API returns it.
                  </Typography>
                )}
              </Box>
            </AccordionDetails>
          </Accordion> */}

          <Button variant="text" className="mfa-back-link" onClick={onCancel}>
            Back to login
          </Button>
        </Grid>

        <Grid item xs={12} lg={5} className="mfa-col mfa-col--guide">
          <Box className="mfa-guide-panel">
            <Typography component="h2" className="mfa-guide-title">
              How to set it up?
            </Typography>

            <Box className="mfa-step-item">
              <Box className="mfa-step-num">1</Box>
              <Box className="mfa-step-body">
                <Box className="mfa-step-head">
                  <DownloadOutlinedIcon className="mfa-step-icon" />
                  <Typography component="span" className="mfa-step-heading">
                    Download an authenticator app
                  </Typography>
                </Box>
                <Typography className="mfa-step-text">
                  Install either Microsoft Authenticator or Google Authenticator on your mobile device.
                </Typography>
              </Box>
            </Box>

            <Box className="mfa-step-item">
              <Box className="mfa-step-num">2</Box>
              <Box className="mfa-step-body">
                <Box className="mfa-step-head">
                  <QrCodeScannerIcon className="mfa-step-icon" />
                  <Typography component="span" className="mfa-step-heading">
                    Scan the QR code
                  </Typography>
                </Box>
                <Typography className="mfa-step-text">
                  Open the app and scan this QR code to add your account.
                </Typography>
              </Box>
            </Box>

            <Box className="mfa-step-item">
              <Box className="mfa-step-num">3</Box>
              <Box className="mfa-step-body">
                <Box className="mfa-step-head">
                  <ShieldOutlinedIcon className="mfa-step-icon" />
                  <Typography component="span" className="mfa-step-heading">
                    Enter the 6-digit code
                  </Typography>
                </Box>
                <Typography className="mfa-step-text">
                  Enter the 6-digit verification code from the app to verify your identity.
                </Typography>
              </Box>
            </Box>

            <Box className="mfa-info-banner">
              <InfoOutlinedIcon className="mfa-info-icon" />
              <Box>
                <Typography className="mfa-info-text">
                  <Box component="span" className="mfa-info-lead">
                    Important:
                  </Box>{' '}
                  This code changes every 30 seconds. Enter the current code from your app.
                </Typography>
              </Box>
            </Box>

            <Box className="mfa-guide-footer">
              <Grid container className="mfa-footer-logos" justifyContent="space-between" alignItems="center">
                <img className="mfa-usaid" src={usLogo} alt="USAID" />
                <img className="mfa-pitc" src={pitcLogo} alt="PITC" />
              </Grid>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default MfaStep;
